import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import DailyClockIn from './DailyClockIn'
import TimeEditModal from './TimeEditModal'
import { getTodayEastern } from '../../lib/dateHelpers'

function fmtET(iso) {
  if (!iso) return '--'
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
}
function fmtHrs(h) { return h != null ? Number(h).toFixed(2) : '0.00' }
function fmtMoney(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
function calcElapsedHrs(clockIn) {
  var ms = Date.now() - new Date(clockIn).getTime()
  return Math.max(0, ms / 3600000)
}
function calcElapsed(clockIn) {
  var ms = Date.now() - new Date(clockIn).getTime()
  if (ms < 0) ms = 0
  var h = Math.floor(ms / 3600000); var m = Math.floor((ms % 3600000) / 60000)
  return h + 'h ' + m + 'm'
}

export default function TimesheetPage() {
  var { profile } = useAuth()
  var isOwner = profile?.role === 'owner'
  var isOffice = profile?.role === 'office_staff'
  if (!isOwner && !isOffice) {
    return <div style={st.container}><div style={st.header}><div style={st.title}>TIME CLOCK</div></div><DailyClockIn /></div>
  }
  return <OwnerView />
}

function OwnerView() {
  var { user, profile } = useAuth()
  var isOwner = profile?.role === 'owner'
  var [tab, setTab] = useState('hours')
  var [attendance, setAttendance] = useState([])
  var [workers, setWorkers] = useState([])
  var [wagesMap, setWagesMap] = useState({})
  var [paidRecords, setPaidRecords] = useState([])
  var [payHistory, setPayHistory] = useState([])
  var [expanded, setExpanded] = useState(null)
  var [expandedSessions, setExpandedSessions] = useState([])
  var [editModal, setEditModal] = useState(null)
  var [wageEdit, setWageEdit] = useState(null)
  var [wageVal, setWageVal] = useState('')
  var [paying, setPaying] = useState(null)
  var [clockOutModal, setClockOutModal] = useState(null)
  var [clockOutTime, setClockOutTime] = useState('')
  var [clockOutLoading, setClockOutLoading] = useState(false)
  // eslint-disable-next-line no-unused-vars
  var [now, setNow] = useState(new Date())
  var intervalRef = useRef(null)

  var fetchAll = useCallback(async function() {
    // ALL attendance records (not filtered by date)
    var { data: att, error: attErr } = await supabase.from('daily_attendance')
      .select('user_id, work_date, total_hours, clock_in, clock_out, status, session_number, id, edited_at, edit_reason, original_clock_in, original_clock_out')
      .order('work_date', { ascending: false })
    if (attErr) console.error('Attendance query error:', attErr)
    else console.log('Attendance data:', (att || []).length, 'records')
    setAttendance(att || [])

    // All paid records
    var { data: pr } = await supabase.from('payroll_records')
      .select('user_id, pay_period_end, status, total_hours, gross_pay, paid_at')
      .eq('status', 'paid')
    setPaidRecords(pr || [])

    // All payroll history (for history tab)
    var { data: ph } = await supabase.from('payroll_records')
      .select('*').order('paid_at', { ascending: false })
    setPayHistory(ph || [])

    // Wages (owner only — RLS enforced)
    if (isOwner) {
      var { data: wages } = await supabase.from('worker_wages').select('user_id, hourly_wage')
      var wm = {}; (wages || []).forEach(function(w) { wm[w.user_id] = parseFloat(w.hourly_wage) || 0 }); setWagesMap(wm)
    }

    // Active workers
    var { data: wk } = await supabase.from('profiles')
      .select('id, full_name, role, is_active')
      .in('role', ['owner', 'office_staff', 'project_manager', 'crew_leader', 'laborer'])
    setWorkers(wk || [])
  }, [isOwner])

  useEffect(function() { fetchAll() }, [fetchAll])

  // Refresh every 30s
  useEffect(function() {
    intervalRef.current = setInterval(function() { setNow(new Date()); fetchAll() }, 30000)
    return function() { clearInterval(intervalRef.current) }
  }, [fetchAll])

  // Realtime subscription
  useEffect(function() {
    var ch = supabase.channel('ts-live-v2').on('postgres_changes', { event: '*', schema: 'public', table: 'daily_attendance' }, function() { fetchAll() }).subscribe()
    return function() { supabase.removeChannel(ch) }
  }, [fetchAll])

  // For each worker, calculate unpaid hours
  var getWorkerUnpaid = function(workerId) {
    // Find last paid date for this worker
    var workerPaid = paidRecords.filter(function(p) { return p.user_id === workerId })
    var lastPaidDate = null
    workerPaid.forEach(function(p) { if (!lastPaidDate || p.pay_period_end > lastPaidDate) lastPaidDate = p.pay_period_end })

    // Sum attendance records AFTER last paid date (or ALL if never paid)
    var unpaidRecords = attendance.filter(function(a) {
      if (a.user_id !== workerId) return false
      if (lastPaidDate && a.work_date <= lastPaidDate) return false
      return true
    })

    var completedHours = unpaidRecords.reduce(function(s, a) { return s + (parseFloat(a.total_hours) || 0) }, 0)
    // Add live elapsed for any open sessions
    var liveHours = 0
    unpaidRecords.forEach(function(a) {
      if (a.status === 'clocked_in' && !a.clock_out && a.clock_in) {
        liveHours += calcElapsedHrs(a.clock_in)
      }
    })

    return {
      hours: completedHours + liveHours,
      completedHours: completedHours,
      liveHours: liveHours,
      records: unpaidRecords,
      firstDate: unpaidRecords.length > 0 ? unpaidRecords[unpaidRecords.length - 1].work_date : null,
      lastPaidDate: lastPaidDate
    }
  }

  // Pay a worker
  var handlePay = async function(worker) {
    var unpaid = getWorkerUnpaid(worker.id)
    var wage = wagesMap[worker.id] || 0
    if (unpaid.completedHours <= 0) return
    var gross = unpaid.completedHours * wage
    if (!window.confirm('Mark ' + worker.full_name + ' as paid for ' + fmtHrs(unpaid.completedHours) + ' hours (' + fmtMoney(gross) + ')?')) return
    setPaying(worker.id)
    var todayStr = getTodayEastern()
    await supabase.from('payroll_records').insert({
      user_id: worker.id,
      pay_period_start: unpaid.firstDate || todayStr,
      pay_period_end: todayStr,
      total_hours: unpaid.completedHours,
      hourly_wage: wage,
      gross_pay: gross,
      status: 'paid',
      paid_at: new Date().toISOString(),
      paid_by: user.id
    })
    setPaying(null)
    fetchAll()
  }

  // Pay ALL
  var handlePayAll = async function() {
    var toPay = workers.filter(function(w) {
      var u = getWorkerUnpaid(w.id)
      return u.completedHours > 0 && (wagesMap[w.id] || 0) > 0
    })
    if (toPay.length === 0) { alert('No workers with unpaid hours and wages set'); return }
    var totalOwed = toPay.reduce(function(s, w) { return s + getWorkerUnpaid(w.id).completedHours * (wagesMap[w.id] || 0) }, 0)
    if (!window.confirm('Pay ' + toPay.length + ' workers — ' + fmtMoney(totalOwed) + ' total?')) return
    setPaying('all')
    var todayStr = getTodayEastern()
    for (var i = 0; i < toPay.length; i++) {
      var w = toPay[i]; var u = getWorkerUnpaid(w.id); var wage = wagesMap[w.id] || 0
      await supabase.from('payroll_records').insert({
        user_id: w.id, pay_period_start: u.firstDate || todayStr, pay_period_end: todayStr,
        total_hours: u.completedHours, hourly_wage: wage, gross_pay: u.completedHours * wage,
        status: 'paid', paid_at: new Date().toISOString(), paid_by: user.id
      })
    }
    alert('Paid ' + toPay.length + ' workers — ' + fmtMoney(totalOwed))
    setPaying(null)
    fetchAll()
  }

  // Save wage
  var handleSaveWage = async function() {
    if (!wageEdit) return
    var val = parseFloat(wageVal)
    if (isNaN(val) || val < 0) return
    await supabase.from('worker_wages').upsert({ user_id: wageEdit, hourly_wage: val, updated_at: new Date().toISOString(), updated_by: user.id })
    setWagesMap(function(prev) { return Object.assign({}, prev, { [wageEdit]: val }) })
    setWageEdit(null)
  }

  // Expand worker detail
  var toggleExpand = function(workerId) {
    if (expanded === workerId) { setExpanded(null); return }
    setExpanded(workerId)
    var unpaid = getWorkerUnpaid(workerId)
    setExpandedSessions(unpaid.records)
  }

  // Owner clock out
  var ownerClockOut = async function(record, timeStr) {
    setClockOutLoading(true)
    var ts = new Date(record.work_date + 'T' + timeStr + ':00-04:00')
    await supabase.from('daily_attendance').update({
      clock_out: ts.toISOString(), status: 'clocked_out',
      edited_by: user.id, edited_at: new Date().toISOString(), edit_reason: 'Clocked out by owner'
    }).eq('id', record.id)
    setClockOutModal(null); setClockOutLoading(false); fetchAll()
  }

  var ownerClockOutAll = async function(timeStr) {
    setClockOutLoading(true)
    var open = attendance.filter(function(a) { return a.status === 'clocked_in' && !a.clock_out })
    for (var i = 0; i < open.length; i++) {
      var ts = new Date(open[i].work_date + 'T' + timeStr + ':00-04:00')
      await supabase.from('daily_attendance').update({
        clock_out: ts.toISOString(), status: 'clocked_out',
        edited_by: user.id, edited_at: new Date().toISOString(), edit_reason: 'Clocked out by owner (batch)'
      }).eq('id', open[i].id)
    }
    alert('Clocked out ' + open.length + ' open sessions')
    setClockOutModal(null); setClockOutLoading(false); fetchAll()
  }

  // Computed totals
  var workerRows = workers.filter(function(w) { return w.role !== 'owner' }).map(function(w) {
    var u = getWorkerUnpaid(w.id)
    var wage = wagesMap[w.id] || 0
    return { worker: w, unpaid: u, wage: wage, owed: u.hours * wage }
  }).sort(function(a, b) { return b.unpaid.hours - a.unpaid.hours })

  var totalUnpaidHrs = workerRows.reduce(function(s, r) { return s + r.unpaid.hours }, 0)
  var totalOwed = workerRows.reduce(function(s, r) { return s + r.owed }, 0)
  var avgCostHr = totalUnpaidHrs > 0 ? totalOwed / totalUnpaidHrs : 0

  var openSessions = attendance.filter(function(a) { return a.status === 'clocked_in' && !a.clock_out })

  return (
    <div style={st.container}>
      <div style={st.header}>
        <div><div style={st.title}>TIMESHEET</div><div style={st.subtitle}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div></div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {['hours', 'history'].map(function(t) {
            return <button key={t} onClick={function() { setTab(t) }} style={{ ...st.tab, ...(tab === t ? st.tabActive : {}) }}>{t === 'hours' ? 'HOURS OWED' : 'PAY HISTORY'}</button>
          })}
        </div>
      </div>

      {/* Worker's own clock in/out */}
      <DailyClockIn />

      {/* === HOURS OWED TAB === */}
      {tab === 'hours' && <div>
        {/* Clock out all button */}
        {openSessions.length > 0 && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', alignSelf: 'center' }}>{openSessions.length} open session{openSessions.length > 1 ? 's' : ''}</span>
          <button onClick={function() { setClockOutTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })); setClockOutModal('all') }} style={st.redBtn}>CLOCK OUT ALL</button>
        </div>}

        {/* Worker rows */}
        {workerRows.map(function(row) {
          var w = row.worker; var u = row.unpaid; var wage = row.wage
          var hasOpenSession = u.records.some(function(r) { return r.status === 'clocked_in' && !r.clock_out })
          var openRec = hasOpenSession ? u.records.find(function(r) { return r.status === 'clocked_in' && !r.clock_out }) : null

          return <div key={w.id}>
            <div style={{ ...st.workerRow, borderLeft: hasOpenSession ? '3px solid #22c55e' : u.hours > 0 ? '3px solid rgba(255,255,255,0.1)' : '3px solid transparent' }} onClick={function() { toggleExpand(w.id) }}>
              {/* Status dot */}
              {hasOpenSession && <div style={{ ...st.dot, backgroundColor: '#22c55e' }} />}

              {/* Name */}
              <div style={{ flex: 1 }}>
                <div style={{ ...st.workerName, color: u.hours > 0 ? '#fff' : 'rgba(255,255,255,0.3)' }}>{w.full_name}</div>
                {hasOpenSession && openRec && <div style={{ fontSize: '11px', color: '#22c55e' }}>Working \u2014 {calcElapsed(openRec.clock_in)}</div>}
              </div>

              {/* Hours */}
              <div style={{ textAlign: 'right', minWidth: '70px' }}>
                <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '16px', fontWeight: 700, color: u.hours > 0 ? '#fff' : 'rgba(255,255,255,0.15)' }}>{fmtHrs(u.hours)}</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.25)' }}>hrs</div>
              </div>

              {/* Wage */}
              <div style={{ textAlign: 'right', minWidth: '60px' }}>
                {isOwner && wage > 0 ? (
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>${wage}/hr</div>
                ) : isOwner ? (
                  <button onClick={function(e) { e.stopPropagation(); setWageEdit(w.id); setWageVal('') }} style={st.setWageBtn}>Set wage</button>
                ) : null}
              </div>

              {/* Owed */}
              <div style={{ textAlign: 'right', minWidth: '70px' }}>
                {isOwner && wage > 0 && u.hours > 0 ? (
                  <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, color: '#b48c3c' }}>{fmtMoney(row.owed)}</div>
                ) : <div style={{ color: 'rgba(255,255,255,0.1)' }}>\u2014</div>}
              </div>

              {/* Paid button */}
              {isOwner && u.completedHours > 0 && wage > 0 && (
                <button onClick={function(e) { e.stopPropagation(); handlePay(w) }} disabled={paying === w.id} style={st.paidBtn}>
                  {paying === w.id ? '...' : 'PAID \u2713'}
                </button>
              )}

              {/* Clock out button for open sessions */}
              {isOwner && hasOpenSession && openRec && (
                <button onClick={function(e) { e.stopPropagation(); setClockOutTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })); setClockOutModal({ record: openRec, name: w.full_name }) }} style={st.smallRedBtn}>CLOCK OUT</button>
              )}
            </div>

            {/* Expanded detail */}
            {expanded === w.id && expandedSessions.length > 0 && (function() {
              // Group by work_date
              var byDate = {}
              expandedSessions.forEach(function(r) {
                if (!byDate[r.work_date]) byDate[r.work_date] = []
                byDate[r.work_date].push(r)
              })
              var dates = Object.keys(byDate).sort().reverse()
              return <div style={st.expandBox}>
                {dates.map(function(dateStr) {
                  var sessions = byDate[dateStr]
                  var dayTotal = sessions.reduce(function(s, r) { return s + (parseFloat(r.total_hours) || 0) }, 0)
                  var dayLabel = new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                  return <div key={dateStr} style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '4px' }}>
                      <span>{dayLabel}</span>
                      <span>{fmtHrs(dayTotal)} hrs</span>
                    </div>
                    {sessions.map(function(s) {
                      var isLive = s.status === 'clocked_in' && !s.clock_out
                      return <div key={s.id} style={st.sessionRow}>
                        <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>S{s.session_number}:</span>
                        <button onClick={function(e) { e.stopPropagation(); setEditModal({ record: s, field: 'clock_in' }) }} style={st.goldLink}>{fmtET(s.clock_in)}</button>
                        <span style={{ color: 'rgba(255,255,255,0.12)' }}>\u2014</span>
                        {isLive ? <span style={{ color: '#22c55e', fontSize: '12px' }}>{String.fromCodePoint(0x23F1)} {calcElapsed(s.clock_in)}</span>
                          : <button onClick={function(e) { e.stopPropagation(); setEditModal({ record: s, field: 'clock_out' }) }} style={st.goldLink}>{fmtET(s.clock_out)}</button>}
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)' }}>({fmtHrs(isLive ? calcElapsedHrs(s.clock_in) : s.total_hours)})</span>
                        {s.edited_at && <span style={st.editBadge}>Edited</span>}
                      </div>
                    })}
                  </div>
                })}
              </div>
            })()}
          </div>
        })}

        {/* Totals bar */}
        <div style={st.totalsBar}>
          <div style={st.totalItem}><div style={st.totalNum}>{fmtHrs(totalUnpaidHrs)}</div><div style={st.totalLabel}>UNPAID HRS</div></div>
          <div style={st.totalItem}><div style={{ ...st.totalNum, color: '#b48c3c' }}>{fmtMoney(totalOwed)}</div><div style={st.totalLabel}>TOTAL OWED</div></div>
          <div style={st.totalItem}><div style={st.totalNum}>{avgCostHr > 0 ? fmtMoney(avgCostHr) : '\u2014'}</div><div style={st.totalLabel}>AVG $/HR</div></div>
          {isOwner && totalOwed > 0 && <button onClick={handlePayAll} disabled={paying === 'all'} style={{ ...st.paidBtn, padding: '10px 20px', fontSize: '12px' }}>{paying === 'all' ? 'PAYING...' : 'PAY ALL \u2713'}</button>}
        </div>
      </div>}

      {/* === PAY HISTORY TAB === */}
      {tab === 'history' && <div>
        <div style={st.secTitle}>PAYROLL HISTORY</div>
        {payHistory.length === 0 && <div style={st.empty}>No payments recorded yet. Use the HOURS OWED tab to mark workers as paid.</div>}
        {(function() {
          // Group by user
          var byUser = {}
          payHistory.forEach(function(p) {
            if (!byUser[p.user_id]) byUser[p.user_id] = []
            byUser[p.user_id].push(p)
          })
          var workerSummaries = Object.keys(byUser).map(function(uid) {
            var records = byUser[uid]
            var w = workers.find(function(w2) { return w2.id === uid })
            var totalHrs = records.reduce(function(s, r) { return s + (parseFloat(r.total_hours) || 0) }, 0)
            var totalPay = records.reduce(function(s, r) { return s + (parseFloat(r.gross_pay) || 0) }, 0)
            var lastPaid = records[0]?.paid_at
            return { uid: uid, name: w?.full_name || 'Unknown', totalHrs: totalHrs, totalPay: totalPay, lastPaid: lastPaid, records: records }
          }).sort(function(a, b) { return b.totalPay - a.totalPay })

          var seasonHrs = workerSummaries.reduce(function(s, w) { return s + w.totalHrs }, 0)
          var seasonPay = workerSummaries.reduce(function(s, w) { return s + w.totalPay }, 0)

          return <div>
            {workerSummaries.map(function(ws) {
              var isExp = expanded === 'hist-' + ws.uid
              return <div key={ws.uid}>
                <div style={st.workerRow} onClick={function() { setExpanded(isExp ? null : 'hist-' + ws.uid) }}>
                  <div style={{ flex: 1 }}><div style={st.workerName}>{ws.name}</div></div>
                  <div style={{ textAlign: 'right', minWidth: '80px' }}><div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, color: '#fff' }}>{fmtHrs(ws.totalHrs)} hrs</div></div>
                  <div style={{ textAlign: 'right', minWidth: '80px' }}><div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 700, color: '#22c55e' }}>{fmtMoney(ws.totalPay)}</div></div>
                  <div style={{ textAlign: 'right', minWidth: '70px', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{ws.lastPaid ? new Date(ws.lastPaid).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '\u2014'}</div>
                </div>
                {isExp && <div style={st.expandBox}>
                  {ws.records.map(function(r) {
                    return <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '12px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>{r.paid_at ? new Date(r.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}</span>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>{fmtHrs(r.total_hours)} hrs \u00D7 ${r.hourly_wage}/hr</span>
                      <span style={{ color: '#22c55e', fontWeight: 600 }}>{fmtMoney(r.gross_pay)} {String.fromCodePoint(0x2713)}</span>
                    </div>
                  })}
                </div>}
              </div>
            })}
            {seasonPay > 0 && <div style={st.totalsBar}>
              <div style={st.totalItem}><div style={st.totalNum}>{fmtHrs(seasonHrs)}</div><div style={st.totalLabel}>SEASON HRS</div></div>
              <div style={st.totalItem}><div style={{ ...st.totalNum, color: '#22c55e' }}>{fmtMoney(seasonPay)}</div><div style={st.totalLabel}>SEASON TOTAL</div></div>
            </div>}
          </div>
        })()}
      </div>}

      {/* Edit modal */}
      {editModal && <TimeEditModal record={editModal.record} field={editModal.field} onSave={function() { setEditModal(null); fetchAll() }} onClose={function() { setEditModal(null) }} />}

      {/* Wage edit modal */}
      {wageEdit && <div style={st.modalOverlay} onClick={function() { setWageEdit(null) }}>
        <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '16px', fontWeight: 700, letterSpacing: '2px', color: '#fff', marginBottom: '16px' }}>SET HOURLY WAGE</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>{(workers.find(function(w) { return w.id === wageEdit }) || {}).full_name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '18px' }}>$</span>
            <input type="number" step="0.50" min="0" value={wageVal} onChange={function(e) { setWageVal(e.target.value) }} placeholder="18.00" autoFocus style={{ flex: 1, padding: '12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#fff', fontSize: '18px', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, outline: 'none' }} />
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '14px' }}>/hr</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={function() { setWageEdit(null) }} style={{ padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' }}>CANCEL</button>
            <button onClick={handleSaveWage} style={{ flex: 1, padding: '10px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' }}>SAVE</button>
          </div>
        </div>
      </div>}

      {/* Clock out modal */}
      {clockOutModal && <div style={st.modalOverlay} onClick={function() { setClockOutModal(null) }}>
        <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '16px', fontWeight: 700, letterSpacing: '2px', color: '#fff', marginBottom: '16px' }}>
            {clockOutModal === 'all' ? 'CLOCK OUT ALL' : 'CLOCK OUT ' + (clockOutModal.name || '').toUpperCase()}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' }}>TIME (EASTERN)</label>
            <input type="time" value={clockOutTime} onChange={function(e) { setClockOutTime(e.target.value) }} style={{ width: '100%', padding: '12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#fff', fontSize: '16px', fontFamily: "'Barlow Condensed',sans-serif", outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={function() { setClockOutModal(null) }} style={{ padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' }}>CANCEL</button>
            <button disabled={clockOutLoading || !clockOutTime} onClick={function() { if (clockOutModal === 'all') ownerClockOutAll(clockOutTime); else ownerClockOut(clockOutModal.record, clockOutTime) }} style={{ flex: 1, padding: '10px', backgroundColor: '#ef4444', border: 'none', borderRadius: '3px', color: '#fff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer', opacity: clockOutLoading ? 0.5 : 1 }}>{clockOutLoading ? '...' : 'CONFIRM'}</button>
          </div>
        </div>
      </div>}
    </div>
  )
}

var st = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#fff' },
  subtitle: { fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  tab: { padding: '8px 16px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  tabActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  secTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' },
  workerRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '3px', cursor: 'pointer', flexWrap: 'wrap' },
  workerName: { fontSize: '14px', fontWeight: 600, color: '#fff' },
  dot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  setWageBtn: { background: 'none', border: 'none', color: '#ef4444', fontSize: '11px', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, letterSpacing: '1px', cursor: 'pointer', textDecoration: 'underline' },
  paidBtn: { padding: '6px 12px', backgroundColor: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '3px', color: '#22c55e', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  redBtn: { padding: '6px 12px', backgroundColor: '#ef4444', border: 'none', borderRadius: '3px', color: '#fff', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  smallRedBtn: { padding: '5px 10px', backgroundColor: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '3px', color: '#ef4444', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' },
  expandBox: { padding: '10px 14px 10px 20px', marginBottom: '6px', backgroundColor: 'rgba(255,255,255,0.01)', borderLeft: '2px solid rgba(180,140,60,0.2)' },
  sessionRow: { display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0', fontSize: '12px', flexWrap: 'wrap' },
  goldLink: { background: 'none', border: 'none', borderBottom: '1px dashed #b48c3c', color: '#b48c3c', fontSize: '12px', fontWeight: 600, fontFamily: "'Barlow',sans-serif", cursor: 'pointer', padding: '0 1px' },
  editBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', fontWeight: 700, letterSpacing: '1px', color: '#b48c3c', backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '2px 6px', marginLeft: '4px' },
  totalsBar: { display: 'flex', alignItems: 'center', gap: '20px', padding: '16px 20px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', marginTop: '20px', flexWrap: 'wrap' },
  totalItem: { textAlign: 'center' },
  totalNum: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '20px', fontWeight: 700, color: '#fff' },
  totalLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' },
  empty: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '14px' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '24px', width: '100%', maxWidth: '400px' },
}
